import type { CommerceLayerClient } from '@commercelayer/sdk'
import { toRefs } from '@vueuse/core'
import type { SizeData } from '@design-system/components/Pdp/PdpFitFinder.props'

/**
 * Composable for managing quick buy functionality for a product.
 *
 * @param {Ref<string>} product - Reference to the product SKU or ID.
 * @returns {Object} An object containing product details, sizes, and related information.
 *
 * @example
 * const { title, price, sizeList } = await useQuickBuy(productRef)
 *
 * @remarks
 * This composable fetches comprehensive product information from multiple sources:
 * - Algolia for product details
 * - Commerce Layer for pricing and stock
 * - Custom API for size data
 */
export const useQuickBuy = async (product: Ref<string>) => {
  const { lineItems } = useCart()
  const { setCartEventsPayload } = useGACartEvents()
  const appConfig = useAppConfig()
  const algoliaIndex = appConfig.currentAlgoliaIndex
  const { $cl } = useNuxtApp()
  const { pdpPath } = useRouteHelper()

  const getSizeData = async (items: string[], sizeTable: string) => {
    //@ts-ignore
    const data: Record<string, SizeData> = await $fetch(
      '/api/getSizesBySizeFile/getSizesByCode',
      { method: 'POST', body: { sizeCodes: items, sizeTable } }
    )
    return Object.keys(data).map((key: string) => {
      return data[key]
    })
  }

  // Fetch product data using async data to fetch multiple things at once, instead of useFetch
  const { data } = await useAsyncData(
    async () => {
      // Fetch Algolia product details
      const algoliaProduct = await $fetch(
        `/api/getProduct/${algoliaIndex}/${product.value}`
      )

      if (!algoliaProduct) throw new Error('Error: product not found')
      setCartEventsPayload({ algoliaObjects: [algoliaProduct] })

      // Prepare SKU list for Commerce Layer
      const skuList = algoliaProduct.size
        .map(item => item.SKU)
        .filter(skuCode => skuCode !== undefined)

      // Fetch Commerce Layer prices and stocks
      const clPricesAndStocks = skuList.length
        ? (
            await Promise.all(
              chunk(skuList, 24).map(async skuCodesChunk => {
                try {
                  return await $cl.skus.list({
                    include: [
                      'prices',
                      'stock_items',
                      'stock_items.reserved_stock',
                    ],
                    filters: {
                      code_in: skuList?.toString() ?? '',
                    },
                    pageSize: 24,
                  })
                } catch (error) {
                  console.error('Error fetching SKUs:', error)
                  return []
                }
              })
            )
          ).flat()
        : []

      // Fetch alternative color products
      const altColorProducts = algoliaProduct['alternative-color']?.length
        ? await $fetch(`/api/getProducts/${algoliaIndex}`, {
            query: {
              mfcList: algoliaProduct['alternative-color']
                .map(element => element.objectID)
                .join(','),
            },
          }).catch(() => null)
        : null

      return {
        algoliaProduct,
        altColorProducts,
        clPricesAndStocks,
      }
    },
    {
      watch: [product],
      transform: async ({
        algoliaProduct,
        altColorProducts,
        clPricesAndStocks,
      }: {
        algoliaProduct: any
        altColorProducts: any
        clPricesAndStocks: any
      }) => {
        const algoliaAssets = algoliaProduct.assets

        const computedImages = getFormattedImages(algoliaAssets)

        const isDisabled = (skuCode?: string) => {
          const sku =
            clPricesAndStocks?.find((sku: any) => sku.code === skuCode) ?? false
          return !sku || !isInStock(sku)
        }

        const title = algoliaProduct.Name
        const sizeGridCode = algoliaProduct.sizeTable

        const price = (() => {
          const skuPrice = clPricesAndStocks
            ?.find((sku: any) => sku.code === product.value)
            ?.prices?.find((price: any) => price.sku_code === product.value)
          const amountFloat = skuPrice?.amount_float ?? algoliaProduct.FullPrice
          return amountFloat
        })()

        const selectedColor = algoliaProduct.ColorDesc!

        const sizes = algoliaProduct.size

        const sizeList: {
          SKU: string
          sku: string
          size: string
          disabled: boolean
          sizePos: string
          sizeCode: string
        }[] = sizes
          .filter(
            (size: any) =>
              typeof size.SKU === 'string' && typeof size.label === 'string'
          )
          .map((size: any) => ({
            sku: size.SKU!,
            SKU: size.SKU!,
            size: size.label ?? '',
            disabled: isDisabled(size.SKU),
            sizePos: size.sizePos ?? '',
            sizeCode: size.sizeCode ?? '',
          }))

        const altColorList = [
          {
            thumbnailUrl: computedImages[0] ?? '',
            color: selectedColor,
            sku: product.value,
            isInStock: isInStock(clPricesAndStocks?.[0]!),
            path: pdpPath(algoliaProduct),
          },
          ...(altColorProducts
            ?.filter(
              //@ts-expect-error
              (altProduct): altProduct is NonNullable<typeof altProduct> =>
                !!altProduct
            )
            ?.map((altProduct: any) => ({
              thumbnailUrl:
                getFirstMediaByPriority(checkMedia(altProduct))
                  ?.split('/')
                  ?.slice(-1)
                  ?.toString() ?? '',
              color: altProduct.ColorDesc ?? '',
              sku: altProduct.objectID,
              isInStock: clPricesAndStocks?.some(
                (el: any) => el.code.includes(product.value) && isInStock(el)
              ),
              path: pdpPath(altProduct),
            })) ?? []),
        ]

        const sizesStocks = algoliaProduct.size.map((size: any) => ({
          sku: size.SKU,
          stock: (size.avRet ?? 0) + (size.avEcom ?? 0),
        }))

        const parseBulletPoints = (description: string) => {
          const bulletPoints = description.split('<br>')
          return bulletPoints
        }
        const productDetail = algoliaProduct.ShortDescription
          ? parseBulletPoints(algoliaProduct.ShortDescription)
          : []
        const sizesArray: string[] = sizeList?.map(size => size.sizeCode) ?? []
        const sizesTable: string = algoliaProduct.sizeTable ?? ''

        const sizeData = await getSizeData(sizesArray, sizesTable)

        return {
          title,
          price,
          selectedColor,
          sizeList,
          computedImages,
          altColorList,
          sizesStocks,
          productDetail,
          sizes,
          sizeData,
          sizeGridCode,
        }
      },
    }
  )

  if (!data.value) {
    throw createError({
      statusCode: 404,
      message: `Product not found`,
    })
  }

  /**
   * Computed size list with cart availability considerations.
   *
   * @type {ComputedRef<Array<SizeListItem>>}
   */
  const sizeList = computed(
    () =>
      data.value?.sizeList?.map((size: any) => {
        const itemInCart = lineItems.value.find(
          item => item.sku_code! === size.SKU
        )
        const isAvailable = itemInCart
          ? itemInCart.quantity < (itemInCart.item.inventory?.quantity ?? 0)
          : true

        return {
          ...size,
          disabled: size.disabled || !isAvailable,
        }
      }) ?? []
  )

  return {
    ...toRefs(data as Ref<NonNullable<typeof data.value>>),
    sizeList,
  }
}
